import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom';
import {
  MessageSquare,
  Users,
  Workflow,
  Hash,
  Database,
  Mail,
  Settings,
  FileText,
  Menu,
  Sparkles,
  PhoneCall,
  ChevronDown,
  Calendar,
  Phone,
  UserCheck,
  BarChart3,
  Target,
  Zap,
  CreditCard,
  Link2,
  MessageCircle,
  Inbox,
  Activity
} from 'lucide-react';
import LeftRailIcon from '../components/LeftRailIcon';
import Logo from '../components/Logo';
import WorkspaceSwitcher from '../components/WorkspaceSwitcher';
import { useWorkspace } from './WorkspaceContext';
import { useSubscription } from '../providers/SubscriptionProvider';
import { INBOUND_FLOWS_ENABLED } from '../config/featureFlags';

/** Collapsed rail width. Icons and the logo stay inside this column when the sidebar opens. */
const RAIL = 70;

function RailSlot({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex shrink-0 items-center justify-center" style={{ width: RAIL }}>
      {children}
    </span>
  );
}

// Feature flag: enables clickable Admin Center (currently hidden)
const ADMIN_CENTER_ENABLED = false; // (import.meta as any).env?.VITE_ADMIN_CENTER_ENABLED === 'true';
// Feature flag: show/hide Inbound section (default ON)
const INBOUND_ENABLED = (import.meta as any).env?.VITE_INBOUND_ENABLED === 'true';

// Lean Inbound Reception section (Dashboard + Activity Log only)
const receptionSection = INBOUND_ENABLED ? {
  id: 'reception',
  type: 'expandable',
  icon: Phone,
  label: 'Reception',
  defaultExpanded: false,
  items: [
    { path: '/app/inbound/dashboard', icon: Activity, label: 'Dashboard', primary: true },
    { path: '/app/inbound/activity', icon: MessageCircle, label: 'Activity Log' },
  ]
} : null;

// 🚀 Shared "core" sections shown at the top of both workspaces
const coreSections = [
  // Quick Actions at top - TEMPORARILY HIDDEN
  // Uncomment below to restore AI Chat and Signals to sidebar
  /*
  {
    id: 'home',
    type: 'single',
    icon: MessageSquare,
    label: 'AI Chat',
    path: '/app'
  },
  {
    id: 'signals',
    type: 'single',
    icon: Zap,
    label: 'Signals',
    path: '/app/signals'
  },
  */
  
  // 🚀 LAUNCHPAD - Getting Started Hub (Home)
  {
    id: 'launchpad',
    type: 'single',
    icon: Sparkles,
    label: 'Launchpad',
    path: '/app',
    primary: true
  },
  
  // HIDDEN: AI Agents (temporarily hidden — configure via Settings → Calling)
  // {
  //   id: 'ai-agents',
  //   type: 'single',
  //   icon: Bot,
  //   label: 'AI Agents',
  //   path: '/app/ai-templates',
  //   primary: true
  // },
  
  // 📚 KNOWLEDGE - Global Company Context (direct link)
  {
    id: 'knowledge',
    type: 'single',
    icon: FileText,
    label: 'Knowledge',
    path: '/app/knowledge',
    primary: true
  }
];

// 📤 OUTBOUND - Unified Outbound System (outbound workspace only)
const outboundCluster = {
  id: 'outbound',
  type: 'expandable', 
  icon: Target,
  label: 'Outbound',
  defaultExpanded: false,
  items: [
    // Quick Call - Fast AI voice outreach (standalone product feature)
    { path: '/app/outbound/quick-call', icon: Zap, label: 'Quick Call', primary: true, disabled: true },
    // CAMPAIGNS - Unified campaign hub (email, multi-channel, + quick call analytics)
    { path: '/app/outbound/campaigns', icon: Workflow, label: 'Campaigns', primary: true },
    { path: '/app/outbound/campaigns?view=templates', icon: FileText, label: 'Templates' },
  ]
};

const inboundCluster = {
  id: 'inbound-campaigns',
  type: 'single',
  icon: Phone,
  label: 'Inbound',
  path: '/app/inbound',
  primary: true,
};

const repliesSection = {
  id: 'replies',
  type: 'single',
  icon: Inbox,
  label: 'Replies',
  path: '/app/replies',
  primary: true,
};

// 👥 CONTACTS — single link (Lead Data hidden; accordion with one child was chrome tax)
const contactsDataSection = {
  id: 'contacts',
  type: 'single',
  icon: Users,
  label: 'Contacts',
  path: '/app/contacts',
  primary: true,
};

// 📱 INFRASTRUCTURE - System Management  (shared)
const infrastructureSection = {
  id: 'infrastructure',
  type: 'expandable',
  icon: Hash,
  label: 'Infrastructure',
  defaultExpanded: false,
  items: [
    { path: '/app/phone-numbers', icon: Phone, label: 'Phone Numbers', primary: true },
    { path: '/app/email-accounts', icon: Mail, label: 'Email Accounts & Warmup' },
    { path: '/app/integrations', icon: Link2, label: 'Integrations' },
    // HIDDEN: Calendar (temporarily hidden, not deleted)
    // { path: '/app/appointments', icon: Calendar, label: 'Calendar' }
  ]
};

const infrastructureSectionInbound = {
  id: 'infrastructure',
  type: 'expandable',
  icon: Hash,
  label: 'Infrastructure',
  defaultExpanded: false,
  items: [
    { path: '/app/phone-numbers', icon: Phone, label: 'Phone Numbers', primary: true },
    { path: '/app/integrations', icon: Link2, label: 'Integrations' },
    // HIDDEN: Calendar (temporarily hidden, not deleted)
    // { path: '/app/appointments', icon: Calendar, label: 'Calendar' }
  ]
};

// Workspace-specific navigation stacks
const inboundFlows = INBOUND_FLOWS_ENABLED ? [inboundCluster] : [];

const navigationSectionsOutbound = [
  ...coreSections,
  outboundCluster,
  ...inboundFlows,
  repliesSection,
  contactsDataSection,
  infrastructureSection
];

const navigationSectionsInbound = receptionSection
  ? [
      ...coreSections,
      receptionSection,
      ...inboundFlows,
      repliesSection,
      contactsDataSection,
      infrastructureSectionInbound
    ]
  : navigationSectionsOutbound;

// Settings & Admin sections (always at bottom)
const bottomSections = [
  {
    id: 'billing',
    type: 'single' as const,
    icon: CreditCard,
    label: 'Billing',
    path: '/app/billing',
  },
  ADMIN_CENTER_ENABLED
    ? {
        id: 'admin',
        type: 'single' as const,
        icon: Settings,
        label: 'Admin Center',
        path: '/app/admin',
        disabled: false
      }
    : {
        id: 'settings',
        type: 'single' as const,
        icon: Settings,
        label: 'Settings',
        path: '/app/settings',
      },
];

const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { workspace } = useWorkspace();
  const { entitlements } = useSubscription();
  const [expandedSections, setExpandedSections] = useState(
    new Set() // No sections expanded by default
  );

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const isPathActive = (path) => {
    const [pathname, search = ''] = String(path).split('?');
    if (pathname === '/' && location.pathname === '/') return true;
    if (pathname === '/app/inbound' && (location.pathname === '/app/inbound' || location.pathname === '/app/inbound/new')) {
      return true;
    }
    if (location.pathname !== pathname) return false;
    const wanted = new URLSearchParams(search);
    const current = new URLSearchParams(location.search);
    if ([...wanted.keys()].length === 0) {
      if (pathname === '/app/outbound/campaigns' && current.get('view') === 'templates') {
        return false;
      }
      return true;
    }
    for (const [key, value] of wanted.entries()) {
      if (current.get(key) !== value) return false;
    }
    return true;
  };

  const isSectionActive = (section) => {
    if (section.type === 'single') {
      return isPathActive(section.path);
    }
    // Highlight section if any of its items (primary or not) is active
    const activeItem = section.items?.find(item => isPathActive(item.path));
    return !!activeItem;
  };

  const navigationForWorkspace = (
    workspace === 'inbound'
      ? navigationSectionsInbound
      : navigationSectionsOutbound
  ).map((section) => {
    if (section.id !== 'infrastructure' || !section.items) return section;
    const items = section.items.filter((item) => {
      if (!entitlements.canUseEmail && item.path === '/app/email-accounts') return false;
      return true;
    });
    return items.length === section.items.length ? section : { ...section, items };
  });

  const renderNavigationItem = (section) => {
    const isActive = isSectionActive(section);
    const isExpanded = expandedSections.has(section.id);
    const IconComponent = section.icon;

    if (section.type === 'single') {
      return (
        <motion.div
          key={section.id}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-1"
          title={collapsed ? section.label : ''} // Tooltip when collapsed
        >
          <Link
            to={section.path}
            className={`relative flex items-center py-2.5 rounded-btn transition-colors duration-200 group ${
              isActive 
                ? 'bg-neutral-90/5 text-neutral-90 ring-1 ring-inset ring-neutral-90/10' 
                : 'text-neutral-90/70 hover:bg-neutral-90/5 hover:text-neutral-90'
            } ${section.primary ? 'font-medium' : ''}`}
          >
            {isActive && (
              <span className="absolute left-0 top-0 z-10 h-full w-1.5 rounded-r bg-gradient-to-b from-primary-40 to-accent-lime" />
            )}
            <RailSlot>
              <IconComponent
                size={18}
                className={`shrink-0 ${isActive ? 'text-primary-40' : 'text-neutral-90/60 group-hover:text-neutral-90'}`}
              />
            </RailSlot>
            {!collapsed && (
              <span className={`min-w-0 pr-4 text-sm leading-tight ${section.labelBreak ? 'whitespace-pre-line' : ''}`}>
                {section.label}
              </span>
            )}
          </Link>
        </motion.div>
      );
    }

    return (
      <motion.div
        key={section.id}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="mb-1"
        title={collapsed ? section.label : ''} // Tooltip when collapsed
      >
        <button
          onClick={() => {
            if (collapsed && onToggle) {
              onToggle(); // Re-expand sidebar first
              setTimeout(() => toggleSection(section.id), 150); // Then expand section
            } else {
              toggleSection(section.id);
            }
          }}
          className={`relative flex w-full items-center py-2.5 rounded-btn transition-colors duration-200 group ${
            isActive 
              ? 'bg-neutral-90/5 text-neutral-90 ring-1 ring-inset ring-neutral-90/10' 
              : 'text-neutral-90/70 hover:bg-neutral-90/5 hover:text-neutral-90'
          }`}
        >
          {isActive && (
            <span className="absolute left-0 top-0 z-10 h-full w-1.5 rounded-r bg-gradient-to-b from-primary-40 to-accent-lime" />
          )}
          <RailSlot>
            <IconComponent
              size={18}
              className={`shrink-0 ${isActive ? 'text-primary-40' : 'text-neutral-90/60 group-hover:text-neutral-90'}`}
            />
          </RailSlot>
          {!collapsed && (
            <span className={`min-w-0 flex-1 pr-2 text-left text-sm font-medium leading-tight ${section.labelBreak ? 'whitespace-pre-line' : ''}`}>
              {section.label}
            </span>
          )}
          {!collapsed && (
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="mr-4 shrink-0"
            >
              <ChevronDown
                size={14}
                className={isActive ? 'text-primary-40' : 'text-neutral-90/40 group-hover:text-neutral-90/60'}
              />
            </motion.div>
          )}
        </button>

        <AnimatePresence>
          {isExpanded && !collapsed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="ml-6 mt-1 space-y-1 overflow-hidden"
            >
              {section.items.map((item) => {
                const ItemIcon = item.icon;
                
                // Handle nested expandable items
                if (item.type === 'expandable') {
                  const isNestedExpanded = expandedSections.has(item.id);
                  const isAnyNestedActive = item.items?.some(nestedItem => isPathActive(nestedItem.path));
                  
                  return (
                    <div key={item.id} className="space-y-1">
                      <button
                        onClick={() => toggleSection(item.id)}
                        className={`relative w-full flex items-center justify-between px-3 py-2 rounded-btn text-sm transition-colors duration-200 group ${
                          isAnyNestedActive 
                            ? 'bg-neutral-90/5 border border-neutral-90/10 text-neutral-90' 
                            : 'text-neutral-90/60 hover:bg-neutral-90/5 hover:text-neutral-90'
                        } ${item.primary ? 'font-medium' : ''}`}
                      >
                        {isAnyNestedActive && (
                          <span className="absolute left-0 top-0 z-10 h-full w-1.5 rounded-r bg-gradient-to-b from-primary-40 to-accent-lime" />
                        )}
                        <div className="flex items-center">
                          <ItemIcon 
                            size={16} 
                            className={`shrink-0 mr-2.5 ${isAnyNestedActive ? 'text-primary-40' : 'text-neutral-90/50 group-hover:text-neutral-90/70'}`} 
                          />
                          <span>{item.label}</span>
                        </div>
                        <motion.div
                          animate={{ rotate: isNestedExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown 
                            size={12} 
                            className={isAnyNestedActive ? 'text-primary-40' : 'text-neutral-90/40 group-hover:text-neutral-90/60'} 
                          />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {isNestedExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="ml-4 space-y-1 overflow-hidden"
                          >
                            {item.items?.map((nestedItem) => {
                              const NestedIcon = nestedItem.icon;
                              const isNestedItemActive = isPathActive(nestedItem.path);
                              
                              return (
                                <Link
                                  key={nestedItem.path}
                                  to={nestedItem.path}
                                  className={`relative flex items-center px-3 py-2 rounded-btn text-sm transition-colors duration-200 group ${
                                    isNestedItemActive 
                                      ? 'bg-neutral-90/5 border border-neutral-90/10 text-neutral-90' 
                                      : 'text-neutral-90/60 hover:bg-neutral-90/5 hover:text-neutral-90'
                                  } ${nestedItem.primary ? 'font-medium' : ''}`}
                                >
                                  {isNestedItemActive && (
                                    <span className="absolute left-0 top-0 z-10 h-full w-1.5 rounded-r bg-gradient-to-b from-primary-40 to-accent-lime" />
                                  )}
                                  <NestedIcon 
                                    size={14} 
                                    className={`shrink-0 mr-2.5 ${isNestedItemActive ? 'text-primary-40' : 'text-neutral-90/50 group-hover:text-neutral-90/70'}`} 
                                  />
                                  <span>{nestedItem.label}</span>
                                </Link>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                
                // Handle regular items
                const isItemActive = isPathActive(item.path);
                if (item.disabled) {
                  return (
                    <span
                      key={item.path}
                      aria-disabled="true"
                      title="Calling is on Enterprise"
                      className={`relative flex items-center px-3 py-2 rounded-btn text-sm text-neutral-90/35 cursor-not-allowed ${item.primary ? 'font-medium' : ''}`}
                    >
                      <ItemIcon size={16} className="shrink-0 mr-2.5 text-neutral-90/30" />
                      <span>{item.label}</span>
                    </span>
                  );
                }
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative flex items-center px-3 py-2 rounded-btn text-sm transition-colors duration-200 group ${
                      isItemActive 
                        ? 'bg-neutral-90/5 border border-neutral-90/10 text-neutral-90' 
                        : 'text-neutral-90/60 hover:bg-neutral-90/5 hover:text-neutral-90'
                    } ${item.primary ? 'font-medium' : ''}`}
                  >
                    {isItemActive && (
                      <span className="absolute left-0 top-0 z-10 h-full w-1.5 rounded-r bg-gradient-to-b from-primary-40 to-accent-lime" />
                    )}
                    <ItemIcon 
                      size={16} 
                      className={`shrink-0 mr-2.5 ${isItemActive ? 'text-primary-40' : 'text-neutral-90/50 group-hover:text-neutral-90/70'}`} 
                    />
                    <span>{item.label}</span>
                    {item.soon && (
                      <span className="ml-auto text-xs bg-neutral-90/10 text-neutral-90/50 px-2 py-0.5 rounded-full">
                        Soon
                      </span>
                    )}
                  </Link>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {!collapsed && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          width: collapsed ? 70 : 280,
        }}
        transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
        className={`fixed left-0 top-0 z-50 h-full bg-white lg:relative lg:z-auto overflow-x-hidden transition-transform duration-300 ${
          collapsed ? 'max-lg:-translate-x-full' : 'max-lg:translate-x-0'
        }`}
        style={{ width: collapsed ? 70 : 280 }} // Ensures width animation on desktop
      >
        <div className="flex h-full flex-col bg-white">
          {/* Header */}
          <div className="flex h-[72px] shrink-0 items-center bg-white">
            <RailSlot>
              <Logo className="h-10 w-10 max-w-none shrink-0" />
            </RailSlot>
            {!collapsed && (
              <motion.span
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="whitespace-nowrap text-xl font-bold text-neutral-90 lowercase"
              >
                halr
              </motion.span>
            )}
            {!collapsed && (
              <button
                onClick={onToggle}
                className="ml-auto mr-4 rounded-card p-1.5 transition-colors hover:bg-bone lg:hidden"
              >
                <Menu size={20} className="text-neutral-90/60" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="sidebar-nav relative flex-1 space-y-2 overflow-y-auto overflow-x-hidden bg-white py-6">
            {/* Workspace switcher (Outbound – Sales vs Inbound – Reception) */}
            <WorkspaceSwitcher
              collapsed={collapsed}
              onRequestExpand={onToggle}
            />

            <Link
              to="/app/odin"
              className="mb-2 flex h-8 items-center transition-opacity hover:opacity-80"
              title="Halr"
            >
              <RailSlot>
                <span className="bg-gradient-to-r from-[#0f4d3f] to-[#5ddb6e] bg-clip-text text-sm font-bold text-transparent">
                  Halr
                </span>
              </RailSlot>
            </Link>

            {navigationForWorkspace.map(renderNavigationItem)}

            {/* Spacer */}
            <div className="h-4" />

            {/* Bottom sections */}
            {bottomSections.map(renderNavigationItem)}
          </nav>

          {/* Footer with toggle button */}
          <div className="shrink-0 bg-white py-2">
            <button
              onClick={onToggle}
              className="flex w-full items-center rounded-btn py-2 text-neutral-90/60 transition-colors hover:bg-bone hover:text-neutral-90"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <RailSlot>
                <Menu size={18} />
              </RailSlot>
              {!collapsed && <span className="text-xs">Collapse</span>}
            </button>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar; 